import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import {
  MessageCircle, Plus, Loader2, Copy, CheckCircle2, XCircle, RefreshCw, Zap,
  Info, Webhook, FileText, ScrollText, Shield, Smartphone, Trash2, PlugZap, Send,
} from "lucide-react";

const STATUS_STYLE = {
  connected: { c: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600", label: "Collegato", dot: "bg-emerald-500" },
  demo: { c: "border-violet-500/40 bg-violet-500/10 text-violet-600", label: "DEMO", dot: "bg-violet-500" },
  pending: { c: "border-amber-500/40 bg-amber-500/10 text-amber-600", label: "Configurazione", dot: "bg-amber-500" },
  error: { c: "border-red-500/40 bg-red-500/10 text-red-600", label: "Errore", dot: "bg-red-500" },
  disconnected: { c: "border-slate-400/40 bg-slate-400/10 text-slate-500", label: "Scollegato", dot: "bg-slate-400" },
};

export default function WhatsApp() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canWrite = (user?.permissions || []).includes("whatsapp.accounts.write");
  const [wizardOpen, setWizardOpen] = useState(false);
  const [detailAcc, setDetailAcc] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["wa-accounts"],
    queryFn: async () => (await api.get("/whatsapp/accounts")).data.data,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["wa-accounts"] }); qc.invalidateQueries({ queryKey: ["wa-channels"] }); };

  const accounts = data?.accounts || [];

  return (
    <div>
      <PageHeader title="Canali WhatsApp" subtitle="Configura e monitora i tuoi numeri WhatsApp Business" icon={MessageCircle}
        actions={canWrite && <Button className="rounded-lg" onClick={() => setWizardOpen(true)} data-testid="add-channel-btn"><Plus className="mr-1 h-4 w-4" /> Collega WhatsApp</Button>} />

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-52 rounded-xl" />)}</div>
      ) : accounts.length === 0 ? (
        <Card className="border-dashed" data-testid="wa-empty">
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary"><PlugZap className="h-8 w-8" /></div>
            <h3 className="font-heading text-xl font-semibold">Nessun canale WhatsApp</h3>
            <p className="max-w-md text-sm text-muted-foreground">Connectivity usa le API ufficiali di WhatsApp Business Cloud. Collega un account reale oppure prova la Modalità Demo.</p>
            {canWrite && <Button className="rounded-lg" onClick={() => setWizardOpen(true)} data-testid="add-channel-empty"><Plus className="mr-1 h-4 w-4" /> Collega WhatsApp</Button>}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {accounts.map((a) => {
            const st = STATUS_STYLE[a.provider === "demo" ? "demo" : a.health_status === "healthy" ? "connected" : a.account_status] || STATUS_STYLE.pending;
            return (
              <Card key={a.id} className="flex flex-col" data-testid={`wa-account-${a.id}`}>
                <CardContent className="flex flex-1 flex-col p-5">
                  <div className="mb-3 flex items-start justify-between">
                    <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary"><MessageCircle className="h-5 w-5" /></div>
                    <Badge variant="outline" className={st.c}><span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${st.dot}`} />{st.label}</Badge>
                  </div>
                  <h3 className="font-heading text-base font-semibold">{a.display_name}</h3>
                  <p className="text-xs text-muted-foreground">{a.internal_name}</p>
                  <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                    <p>Ambiente: <span className="font-medium text-foreground capitalize">{a.environment}</span></p>
                    <p>WABA: <span className="font-mono">{a.waba_id || "—"}</span></p>
                    <p>Webhook: {a.webhook_status === "verified" ? "verificato" : "non configurato"}</p>
                  </div>
                  <Button variant="outline" className="mt-4 w-full rounded-lg" onClick={() => setDetailAcc(a)} data-testid={`open-detail-${a.id}`}>Apri dettaglio</Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SetupWizard open={wizardOpen} onOpenChange={setWizardOpen} onDone={refresh} />
      {detailAcc && <ChannelDetail account={detailAcc} onClose={() => setDetailAcc(null)} onChanged={refresh} canWrite={canWrite} />}
    </div>
  );
}

function SetupWizard({ open, onOpenChange, onDone }) {
  const [mode, setMode] = useState("demo");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    internal_name: "", display_name: "", environment: "test", is_default: true,
    business_account_id: "", waba_id: "", phone_number_id: "", app_id: "",
    app_secret: "", access_token: "", verify_token: "", graph_api_version: "v21.0",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.internal_name || !form.display_name) return toast.error("Compila nome interno e visualizzato");
    setLoading(true);
    try {
      const payload = {
        internal_name: form.internal_name, display_name: form.display_name,
        environment: form.environment, is_default: form.is_default, demo: mode === "demo",
        credentials: mode === "real" ? {
          business_account_id: form.business_account_id, waba_id: form.waba_id,
          phone_number_id: form.phone_number_id, app_id: form.app_id, app_secret: form.app_secret,
          access_token: form.access_token, verify_token: form.verify_token, graph_api_version: form.graph_api_version,
        } : null,
      };
      await api.post("/whatsapp/accounts", payload);
      toast.success(mode === "demo" ? "Canale demo creato" : "Account WhatsApp creato");
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const tip = (t) => (
    <TooltipProvider><Tooltip><TooltipTrigger asChild><Info className="ml-1 inline h-3 w-3 text-muted-foreground" /></TooltipTrigger><TooltipContent className="max-w-xs">{t}</TooltipContent></Tooltip></TooltipProvider>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="setup-wizard">
        <DialogHeader><DialogTitle>Collega WhatsApp Business</DialogTitle></DialogHeader>
        <div className="mb-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
          Connectivity utilizza le <strong>API ufficiali di WhatsApp Business Cloud</strong>. In assenza di credenziali reali, usa la <strong>Modalità Demo</strong> (nessuna chiamata a Meta).
        </div>
        <div className="mb-3 grid grid-cols-2 gap-2">
          <button onClick={() => setMode("demo")} className={`rounded-lg border px-3 py-2 text-sm ${mode === "demo" ? "border-primary bg-primary/10 text-primary" : "border-border"}`} data-testid="mode-demo">Modalità Demo</button>
          <button onClick={() => setMode("real")} className={`rounded-lg border px-3 py-2 text-sm ${mode === "real" ? "border-primary bg-primary/10 text-primary" : "border-border"}`} data-testid="mode-real">Credenziali reali</button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nome interno</Label><Input value={form.internal_name} onChange={(e) => set("internal_name", e.target.value)} data-testid="wiz-internal-name" /></div>
            <div className="space-y-1.5"><Label>Nome visualizzato</Label><Input value={form.display_name} onChange={(e) => set("display_name", e.target.value)} data-testid="wiz-display-name" /></div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-1.5">
              <Label>Ambiente</Label>
              <Select value={form.environment} onValueChange={(v) => set("environment", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="test">Test</SelectItem><SelectItem value="production">Produzione</SelectItem></SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 pt-6 text-sm"><Switch checked={form.is_default} onCheckedChange={(v) => set("is_default", v)} /> Predefinito</label>
          </div>
          {mode === "real" && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              <p className="text-xs font-medium text-muted-foreground">Credenziali Meta (cifrate, mostrate solo mascherate)</p>
              <div className="space-y-1.5"><Label>WhatsApp Business Account ID (WABA){tip("L'ID del tuo WhatsApp Business Account nel Business Manager di Meta")}</Label><Input value={form.waba_id} onChange={(e) => set("waba_id", e.target.value)} data-testid="wiz-waba" /></div>
              <div className="space-y-1.5"><Label>Phone Number ID{tip("ID del numero registrato, diverso dal numero di telefono")}</Label><Input value={form.phone_number_id} onChange={(e) => set("phone_number_id", e.target.value)} data-testid="wiz-pnid" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Business Account ID</Label><Input value={form.business_account_id} onChange={(e) => set("business_account_id", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>App ID</Label><Input value={form.app_id} onChange={(e) => set("app_id", e.target.value)} /></div>
              </div>
              <div className="space-y-1.5"><Label>App Secret{tip("Segreto dell'app Meta, usato per verificare la firma dei webhook")}</Label><Input type="password" value={form.app_secret} onChange={(e) => set("app_secret", e.target.value)} /></div>
              <div className="space-y-1.5"><Label>Access Token{tip("Token permanente o di sistema per autenticare le chiamate")}</Label><Input type="password" value={form.access_token} onChange={(e) => set("access_token", e.target.value)} data-testid="wiz-token" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Verify Token</Label><Input value={form.verify_token} onChange={(e) => set("verify_token", e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Graph API</Label><Input value={form.graph_api_version} onChange={(e) => set("graph_api_version", e.target.value)} /></div>
              </div>
            </div>
          )}
        </div>
        <Button onClick={submit} disabled={loading} className="mt-2 w-full rounded-lg" data-testid="wiz-submit">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {mode === "demo" ? "Crea canale demo" : "Salva e collega"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ChannelDetail({ account, onClose, onChanged, canWrite }) {
  const qc = useQueryClient();
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState("");
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/meta/whatsapp`;

  const { data: logs } = useQuery({ queryKey: ["wa-logs", account.id], queryFn: async () => (await api.get("/whatsapp/logs")).data.data });
  const { data: tplData } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/whatsapp/templates")).data.data });

  const act = async (name, fn) => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const test = () => act("test", async () => {
    const { data } = await api.post(`/whatsapp/accounts/${account.id}/test`);
    setTestResult(data.data);
    onChanged();
  });
  const sync = () => act("sync", async () => { await api.post(`/whatsapp/accounts/${account.id}/sync`); toast.success("Sincronizzato"); onChanged(); });
  const syncTpl = () => act("tpl", async () => { await api.post(`/whatsapp/templates/sync?acc_id=${account.id}`); toast.success("Template sincronizzati"); qc.invalidateQueries({ queryKey: ["wa-templates"] }); });
  const disconnect = () => act("disc", async () => { await api.post(`/whatsapp/accounts/${account.id}/disconnect`); toast.success("Disconnesso"); onChanged(); onClose(); });

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="channel-detail">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-primary" /> {account.display_name}</DialogTitle></DialogHeader>
        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview">Panoramica</TabsTrigger>
            <TabsTrigger value="webhook">Webhook</TabsTrigger>
            <TabsTrigger value="templates">Template</TabsTrigger>
            <TabsTrigger value="logs">Log API</TabsTrigger>
            <TabsTrigger value="security">Sicurezza</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Info2 label="Provider" value={account.provider} />
              <Info2 label="Ambiente" value={account.environment} />
              <Info2 label="WABA ID" value={account.waba_id || "—"} />
              <Info2 label="Phone Number ID" value={account.default_phone_number_id || "—"} />
              <Info2 label="Stato" value={account.account_status} />
              <Info2 label="Salute" value={account.health_status} />
            </div>
            <div className="flex flex-wrap gap-2">
              {canWrite && <Button size="sm" variant="outline" onClick={test} disabled={busy === "test"} className="rounded-lg" data-testid="btn-test-connection">{busy === "test" ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Zap className="mr-1 h-3.5 w-3.5" />} Test connessione</Button>}
              {canWrite && <Button size="sm" variant="outline" onClick={sync} disabled={busy === "sync"} className="rounded-lg"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Sincronizza numero</Button>}
              {canWrite && <Button size="sm" variant="outline" onClick={disconnect} disabled={busy === "disc"} className="rounded-lg text-destructive">Disconnetti</Button>}
            </div>
            {testResult && (
              <div className={`rounded-lg border p-3 text-sm ${testResult.success ? "border-emerald-500/40 bg-emerald-500/10" : "border-red-500/40 bg-red-500/10"}`} data-testid="test-result">
                <p className="flex items-center gap-2 font-medium">{testResult.success ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />} {testResult.message}</p>
                {testResult.demo && <p className="mt-1 text-xs text-muted-foreground">Modalità Demo WhatsApp: nessuna chiamata reale a Meta.</p>}
                {testResult.checks && <pre className="mt-2 overflow-x-auto rounded bg-background/60 p-2 text-xs">{JSON.stringify(testResult.checks, null, 2)}</pre>}
              </div>
            )}
          </TabsContent>

          <TabsContent value="webhook" className="space-y-3 text-sm">
            <div className="space-y-1.5">
              <Label>URL callback (inseriscilo su Meta)</Label>
              <div className="flex gap-2">
                <Input readOnly value={webhookUrl} className="font-mono text-xs" data-testid="webhook-url" />
                <Button size="icon" variant="outline" onClick={() => { navigator.clipboard?.writeText(webhookUrl); toast.success("Copiato"); }}><Copy className="h-4 w-4" /></Button>
              </div>
            </div>
            <Info2 label="Verify token" value={account.verify_token_masked || "••••"} />
            <Info2 label="Stato verifica" value={account.webhook_status === "verified" ? "Verificato" : "Non configurato"} />
            <p className="text-xs text-muted-foreground">Campi richiesti su Meta: messages. La firma X-Hub-Signature-256 viene verificata quando è configurato l'App Secret (obbligatoria in produzione).</p>
          </TabsContent>

          <TabsContent value="templates" className="space-y-3">
            {canWrite && <Button size="sm" variant="outline" onClick={syncTpl} disabled={busy === "tpl"} className="rounded-lg" data-testid="sync-templates-detail"><RefreshCw className="mr-1 h-3.5 w-3.5" /> Sincronizza template</Button>}
            <div className="space-y-1.5">
              {(tplData?.templates || []).map((t) => (
                <div key={t.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                  <span>{t.name} <span className="text-xs text-muted-foreground">({t.language})</span></span>
                  <Badge variant="outline" className={t.status === "approved" ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600" : "border-amber-500/40 bg-amber-500/10 text-amber-600"}>{t.status}</Badge>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <div className="max-h-72 overflow-y-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Operazione</TableHead><TableHead>Stato</TableHead><TableHead>ms</TableHead></TableRow></TableHeader>
                <TableBody>
                  {(logs?.logs || []).map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-xs">{new Date(l.created_at).toLocaleString("it-IT")}</TableCell>
                      <TableCell className="text-xs">{l.operation}</TableCell>
                      <TableCell>{l.success ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <XCircle className="h-4 w-4 text-red-600" />}</TableCell>
                      <TableCell className="text-xs">{l.duration_ms ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                  {(!logs?.logs || logs.logs.length === 0) && <TableRow><TableCell colSpan={4} className="py-6 text-center text-xs text-muted-foreground">Nessun log</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="security" className="space-y-2 text-sm">
            <Info2 label="Access Token" value={account.access_token_masked || "non impostato"} />
            <Info2 label="App Secret" value={account.app_secret_masked || "non impostato"} />
            <Info2 label="Verify Token" value={account.verify_token_masked || "auto"} />
            <p className="text-xs text-muted-foreground">Le credenziali sono cifrate a riposo e mai restituite in chiaro dal backend.</p>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Info2({ label, value }) {
  return <div className="flex justify-between gap-3 border-b border-border/50 py-1.5"><span className="text-muted-foreground">{label}</span><span className="font-medium capitalize">{value}</span></div>;
}
